import { createSelector } from "reselect";
import { isUndefined } from "lodash";

import { ICurrentSubscriptionDetails, IState } from "./types";
import { IRootState } from "../reducers";
import { getOfferingDetailsList, parseUtc } from "../../utils/common";
import {
  ICustomerSKUDetails,
  IOfferingDetailsResponse,
  ISKUDetails,
} from "../../services/api/offering/types";
import {
  ISubscription,
  ISubscriptionDetails,
  ISubscriptionOptions,
} from "../../services/api/subscription/types";
import { selectCurrentProductLine } from "../metadata";
import { selectAssignedLicenses } from "../manageUsers";
import { selectCurrentSKU } from "../skuMapping";

export const selectSubscriptionsState = (state: IRootState): IState => {
  return state.subscriptions;
};

export const selectIsFetching = createSelector(
  [selectSubscriptionsState],
  (subscriptionsState: IState): boolean => {
    return subscriptionsState.isFetching;
  },
);

export const selectCurrentSubscriptionDetailLoaded = createSelector(
  [selectSubscriptionsState],
  (subscriptionsState: IState): boolean => {
    return subscriptionsState.isCurrentSubscriptionDetailLoaded;
  },
);

export const selectIsCreateFetching = createSelector(
  [selectSubscriptionsState],
  (subscriptionsState: IState): boolean => {
    return subscriptionsState.isCreateFetching;
  },
);

export const selectIsUpdateFetching = createSelector(
  [selectSubscriptionsState],
  (subscriptionsState: IState): boolean => {
    return subscriptionsState.isUpdateFetching;
  },
);

export const selectIsPauseSubscriptionFetching = createSelector(
  [selectSubscriptionsState],
  (subscriptionsState: IState): boolean => {
    return subscriptionsState.isPauseSubscriptionFetching;
  },
);

export const selectIsCancelFetching = createSelector(
  [selectSubscriptionsState],
  (subscriptionsState: IState): boolean => {
    return subscriptionsState.isCancelFetching;
  },
);

export const selectIsUncancelFetching = createSelector(
  [selectSubscriptionsState],
  (subscriptionsState: IState): boolean => {
    return subscriptionsState.isUncancelFetching;
  },
);

export const selectIsRecycleFetching = createSelector(
  [selectSubscriptionsState],
  (subscriptionsState: IState): boolean => {
    return subscriptionsState.isRecycleFetching;
  },
);

export const selectIsFetchingPreview = createSelector(
  [selectSubscriptionsState],
  (subscriptionsState: IState): boolean => {
    return subscriptionsState.isPreviewFetching;
  },
);

export const selectError = createSelector(
  [selectSubscriptionsState],
  (subscriptionsState: IState): any => {
    return subscriptionsState.error;
  },
);

export const selectOfferingDetails = createSelector(
  [selectSubscriptionsState],
  (subscriptionsState: IState): IOfferingDetailsResponse | undefined => {
    return subscriptionsState.offeringDetails;
  },
);

export const selectSubscriptionList = createSelector(
  [selectSubscriptionsState],
  (subscriptionsState: IState): ISubscription[] => {
    return subscriptionsState.subscriptionList ?? [];
  },
);

// Whether a subscription is over — finished, whatever the provider calls it.
//
// `is_terminated` alone is not that question: ph-api only sets it once the
// provider declares the subscription dead (SubscriptionStandingService, from
// the SP's Terminated state), and Apple withholds that for a year and a day
// past expiry so a StoreKit2 customer can resubscribe onto the same
// OriginalTransactionId — so an Apple subscription dead for months still
// reads `is_terminated: false`.
//
// `will_expire_at` is the provider-agnostic answer: ph-api writes it from the
// SP subscription's `terminated_at` on the Cancelled state and clears it
// otherwise (SubscriptionStandingService.UpdateSubscriptionAsync), so it means
// the same for every provider — future for a cancelled-but-still-served
// subscription, past for a finished one. ph-api reads it this way itself when
// deciding whether an Apple or Amazon subscription still blocks account
// deletion (CustomerService.TerminateOrCancelSubscriptionInSpAsync).
//
// No expiry date means the subscription is running: neither an active one nor
// one merely past due (crypto grace, Recurly dunning, a Google account hold,
// an Apple billing retry) carries one, and those stay listed since the portal
// is where the customer pays to recover them.
export const isSubscriptionOver = (subscription: ISubscription): boolean => {
  if (subscription.is_terminated) {
    return true;
  }

  if (!subscription.will_expire_at) {
    return false;
  }

  // parseUtc, not `new Date`: ph-api serialises will_expire_at as naive UTC
  // ("2026-09-30T00:00:00" — no Z, no offset), and per ES2015 a date-time
  // string with no offset parses as LOCAL time — up to fourteen hours early
  // in a browser east of UTC. For those hours a still-paying customer would
  // be dropped from the Subscriptions tab, handed the "pick a plan" UX, have
  // the payment card's rail unloaded, and be denied /manage-services.
  const expiresAt = parseUtc(subscription.will_expire_at).getTime();

  // An unparseable date is not evidence anything ended, so it leaves the
  // subscription listed rather than hide it on the strength of a bad string.
  return !Number.isNaN(expiresAt) && expiresAt <= Date.now();
};

// All subscriptions a customer currently holds that are still meaningful to show: active ones plus
// those cancelled but still served to the end of their paid period. Anything past its expiry is
// left out whether or not the provider has terminated it — see isSubscriptionOver. Used to let the
// customer see overlapping subscriptions (e.g. a website sub alongside an Apple sub) once the
// AllowMultipleActiveSubscriptions guardrail is lifted.
//
// Memoised on the subscription list, so a subscription expiring while the page sits open
// disappears on the next refetch, not on the clock tick.
export const selectActiveAndRecentlyCancelledSubscriptions = createSelector(
  [selectSubscriptionList],
  (subscriptions: ISubscription[]): ISubscription[] => {
    return [...(subscriptions ?? [])]
      .filter((subscription) => {
        return !isSubscriptionOver(subscription);
      })
      .sort((a, b) => {
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      });
  },
);

export const selectCurrentCustomerSubscription = createSelector(
  [selectSubscriptionList, selectCurrentProductLine],
  (
    subscriptionsState: ISubscription[],
    currentProductLine,
    // `| undefined` is not a formality: a customer whose subscriptions are all
    // over gets nothing back (the "pick a plan" case); a bare ISubscription
    // type would let an unguarded dereference of it slip past the compiler.
  ): ISubscription | undefined => {
    // Skip subscriptions that are over entirely: a terminated one's SP-side row
    // may be gone (e.g. after an admin unlink), which makes /actions/get-detail
    // 404 and breaks the page; an expired-but-not-yet-terminated one (Apple
    // holds that state for a year past expiry) is just as finished. This is
    // the single-subscription path most customers land on, so "expired
    // subscriptions are not listed" must hold here too, not just in the list
    // above. Customers left with nothing fall through to the "pick a plan" UX.
    const sortedList = [...(subscriptionsState ?? [])]
      .filter((subscription) => {
        return (
          subscription?.sku?.product_line_code === currentProductLine?.code &&
          !isSubscriptionOver(subscription)
        );
      })
      .sort((a, b) => {
        return (
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        );
      });

    return sortedList[0];
  },
);

export const selectCurrentSubscriptionDetail = createSelector(
  [
    selectSubscriptionsState,
    selectCurrentCustomerSubscription,
    selectCurrentSKU,
  ],
  (
    { currentSubscriptionDetail }: IState,
    subscription: ISubscription | undefined,
    currentSKU,
  ): ICurrentSubscriptionDetails | undefined => {
    if (!currentSubscriptionDetail || !subscription) {
      return undefined;
    }
    const totalLicenses =
      subscription?.sku?.licenses +
      currentSubscriptionDetail?.sp?.addons?.reduce((currentValue, item) => {
        return currentValue + item.quantity;
      }, 0);

    return {
      ...currentSubscriptionDetail,
      currentSKU,
      skuId: subscription.sku_id,
      isTerminated: subscription.is_terminated,
      isGoodStanding: subscription.is_good_standing,
      licenses: subscription.sku?.licenses || 0,
      offeringCode: subscription.offering_code,
      productLineCode: subscription?.sku?.product_line_code || "",
      totalLicenses: totalLicenses || 0,
      willExpireAt: subscription.will_expire_at,
    };
  },
);

export const selectCurrentSubscriptionId = createSelector(
  [selectCurrentCustomerSubscription],
  (subscription): string | undefined => {
    return subscription?.id;
  },
);

export const selectSubscriptionDetailsById = createSelector(
  [selectSubscriptionsState],
  (subscriptionsState: IState): Record<string, ISubscriptionDetails> => {
    return subscriptionsState.subscriptionDetailsById ?? {};
  },
);

// The payment rail (`sp.payment_provider`, e.g. "crypto") each subscription
// provider is billed on, keyed by upper-cased sp_code.
//
// The rail belongs to the subscription, but the payment page renders one block
// per BILLING ACCOUNT, identified by its sp_code — so this maps one onto the
// other. Reading the rail off whichever subscription is "current" gets a
// multi-provider customer wrong: someone whose current subscription is Recurly
// but who also holds a PH_SUBSCRIPTION account would see the provider's own
// label ("Subscription") on the crypto account. sp_codes reach the portal by
// two routes (the billing-account list and the subscription detail) that agree
// on the value but not its casing, hence upper-casing both sides.
//
// A provider whose subscriptions disagree on the rail is suppressed rather
// than guessed at: with one rail in service that cannot happen today, and if a
// second ships, the account falls back to the provider's own label instead of
// printing one of two instruments at random. Suppression is an explicit
// `null`, not a deleted key, so a caller can tell apart:
//
//   key absent    - no loaded subscription is on this provider; look the
//                   rail up elsewhere.
//   key -> null   - this provider's subscriptions disagree; refused on
//                   purpose, no other source may override it.
//   key -> string - the one rail every subscription on the provider agrees on.
//
// (A deleted key collapsed the first two into "absent", and reading it with
// `??` fell through to the current subscription's rail — printing exactly
// the random instrument this suppression exists to prevent.)
export const selectPaymentRailBySpCode = createSelector(
  [selectSubscriptionDetailsById],
  (detailsById): Record<string, string | null> => {
    const railBySpCode: Record<string, string | null> = {};
    const conflictingSpCodes = new Set<string>();

    Object.values(detailsById ?? {}).forEach((detail) => {
      const spCode = detail?.sp?.sp_code?.toUpperCase();
      const rail = detail?.sp?.payment_provider;

      if (!spCode || !rail) {
        return;
      }

      if (spCode in railBySpCode && railBySpCode[spCode] !== rail) {
        conflictingSpCodes.add(spCode);
      }

      railBySpCode[spCode] = rail;
    });

    conflictingSpCodes.forEach((spCode) => {
      railBySpCode[spCode] = null;
    });

    return railBySpCode;
  },
);

// Merges a subscription list row with its loaded detail into the enriched shape
// ActiveSubscription expects.
const buildSubscriptionDetails = (
  subscription: ISubscription,
  detail: ISubscriptionDetails,
  currentSKU?: ISKUDetails,
): ICurrentSubscriptionDetails => {
  const addonLicenses =
    detail.sp?.addons?.reduce((total, addon) => {
      return total + (addon.quantity ?? 0);
    }, 0) || 0;

  return {
    ...detail,
    currentSKU,
    skuId: subscription.sku_id,
    isTerminated: subscription.is_terminated,
    isGoodStanding: subscription.is_good_standing,
    licenses: subscription.sku?.licenses || 0,
    offeringCode: subscription.offering_code,
    productLineCode: subscription.sku?.product_line_code || "",
    totalLicenses: (subscription.sku?.licenses || 0) + addonLicenses,
    willExpireAt: subscription.will_expire_at,
  };
};

// Builds the enriched ICurrentSubscriptionDetails (the shape ActiveSubscription
// expects) for every active + recently-cancelled subscription in the current product
// line, most-recent first — used to render one subscription block per sub when a
// customer holds more than one. Subs whose detail has not loaded yet are skipped.
export const selectSubscriptionsDetailsForProductLine = createSelector(
  [
    selectActiveAndRecentlyCancelledSubscriptions,
    selectCurrentProductLine,
    selectSubscriptionDetailsById,
  ],
  (
    subscriptions: ISubscription[],
    currentProductLine,
    detailsById,
  ): ICurrentSubscriptionDetails[] => {
    return subscriptions
      .filter((subscription) => {
        return (
          subscription.sku?.product_line_code === currentProductLine?.code &&
          !!detailsById[subscription.id]
        );
      })
      .map((subscription) => {
        return buildSubscriptionDetails(
          subscription,
          detailsById[subscription.id],
        );
      });
  },
);

export const selectSubscriptionWithGeoCurrency = createSelector(
  [selectOfferingDetails, selectCurrentSubscriptionDetail],
  (offeringDetails, activeSubscription): ICustomerSKUDetails[] => {
    if (!offeringDetails) {
      return [];
    }

    const currency = activeSubscription?.sp?.is_active
      ? activeSubscription?.sp?.next_invoice_amount?.currency
      : "USD";

    return getOfferingDetailsList(offeringDetails, currency);
  },
);

export const selectAvailableListSpListWithAddon = createSelector(
  [selectSubscriptionWithGeoCurrency],
  (availableList): ICustomerSKUDetails[] => {
    return (availableList ?? [])
      .filter((item) => {
        return !item.sp.is_addon;
      })
      .map((item) => {
        if (!item.addons.length) {
          return item;
        }

        return {
          ...item,
          availableAddons: availableList.filter((skuDetail) => {
            return item.addons.length && item.addons.includes(skuDetail.sku_id);
          }),
        };
      });
  },
);

export const selectSubscriptionPreviewPrice = createSelector(
  [selectSubscriptionsState],
  (subscriptionsState: IState): number | undefined => {
    return subscriptionsState.subscriptionPreview?.estimate.amount;
  },
);

export const selectSubscriptionOptions = createSelector(
  [selectSubscriptionsState],
  (subscriptionsState: IState): ISubscriptionOptions | undefined => {
    return subscriptionsState.subscriptionOptions;
  },
);

export const selectIsFetchingSubscriptionOptions = createSelector(
  [selectSubscriptionsState],
  (subscriptionsState: IState): boolean => {
    return subscriptionsState.isFetchingOptions;
  },
);

export const selectSubscriptionAutoRecycle = createSelector(
  [selectSubscriptionOptions],
  (option): boolean => {
    return option?.options.auto_recycle || false;
  },
);

export const selectFeatureLicenses = createSelector(
  [selectCurrentSubscriptionDetail, selectAssignedLicenses],
  (subscriptionDetail): number | undefined => {
    const skuLicenses = subscriptionDetail?.upcoming?.sku.licenses || 0;
    const addonsLicenses =
      subscriptionDetail?.upcoming?.addons?.reduce((currentValue, item) => {
        return currentValue + item.quantity;
      }, 0) || 0;

    return skuLicenses + addonsLicenses;
  },
);

export const selectIsShouldUnassignedLicences = createSelector(
  [selectFeatureLicenses, selectAssignedLicenses],
  (featureLicenses, assignedLicense): boolean => {
    if (isUndefined(featureLicenses) || featureLicenses === 0) {
      return false;
    }

    return featureLicenses < assignedLicense;
  },
);

export const selectSubscriptionPreview = createSelector(
  [selectSubscriptionsState],
  (subscriptionsState: IState) => {
    return subscriptionsState.subscriptionPreview;
  },
);
